import React, { useEffect, useState } from "react";
import { Button, Col, Container, Form, Row, Card } from "react-bootstrap";
import FeatureCard from "./components/FeatureCard";
import Select from "react-select";
import { useDispatch } from "react-redux";
import LineChart from "../../../components/Graph/LineChart";
import PieChart from "../../../components/Graph/PieChart";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

const Dashboard = () => {
  const dispatch = useDispatch();
  const [dashBoardData, setDashBoardData] = useState();
  const [revenue, setReveneue] = useState();
  const [characterRevenue, setCharacterReveneue] = useState();
  const [appearanceData, setAppearanceData] = useState();

  // Enhanced filters
  const [filter, setFilter] = useState({ filter: "weekly" });
  const [filterNew, setFilterNew] = useState({ filter: "weekly" });

  // Enhanced financial filtering system
  const [financialFilter, setFinancialFilter] = useState({
    period: "monthly", // daily, weekly, monthly, yearly
    month: new Date().getMonth() + 1,
    year: new Date().getFullYear(),
    customRange: false,
  });

  // date range filter
  const [startDate, setStartDate] = useState(
    new Date(new Date().setDate(new Date().getDate() - 30))
  );
  const [endDate, setEndDate] = useState(new Date());

  // Filter options
  const periodOptions = [
    { value: "daily", label: "Daily" },
    { value: "weekly", label: "Weekly" },
    { value: "monthly", label: "Monthly" },
    { value: "yearly", label: "Yearly" },
  ];

  const monthOptions = Array.from({ length: 12 }, (_, i) => ({
    value: i + 1,
    label: new Date(0, i).toLocaleString("default", { month: "long" }),
  }));

  const yearOptions = Array.from({ length: 10 }, (_, i) => ({
    value: new Date().getFullYear() - i,
    label: (new Date().getFullYear() - i).toString(),
  }));

  const handleDateChange = (dates) => {
    const [start, end] = dates;
    setStartDate(start);
    setEndDate(end);

    // pass to backend filter when both selected
    if (start && end) {
      const newFilter = {
        filter: "custom",
        startDate: start.toISOString().split("T")[0],
        endDate: end.toISOString().split("T")[0],
      };

      setFilter(newFilter);

      setFinancialFilter((prev) => ({
        ...prev,
        period: "custom",
        customRange: true,
        startDate: start.toISOString().split("T")[0],
        endDate: end.toISOString().split("T")[0],
      }));
    }
  };

  const handleFinancialFilterChange = (field, value) => {
    setFinancialFilter((prev) => ({
      ...prev,
      [field]: value,
      ...(field === "period" && value !== "custom"
        ? { customRange: false }
        : {}),
    }));
  };

  const applyFinancialFilters = () => {
    console.log("Applying financial filters:", financialFilter);

    // Create filter object for API calls
    const newFilter = {
      filter: financialFilter.period,
      ...(financialFilter.period !== "yearly" && {
        month: financialFilter.month,
      }),
      year: financialFilter.year,
      ...(financialFilter.customRange && {
        startDate: financialFilter.startDate,
        endDate: financialFilter.endDate,
      }),
    };

    setFilter(newFilter);
    setFilterNew(newFilter);

    // Here you would typically call your API actions:
    // dispatch(getRevenue(newFilter));
    // dispatch(getCharacterRevenue(newFilter));
    // dispatch(getDashboardData(newFilter));
  };

  // Enhanced mock data generators based on current filter
  const generateMockData = (type, currentFilter) => {
    const baseData = {
      subscription: {
        daily: Array.from(
          { length: 30 },
          (_, i) => Math.floor(Math.random() * 500) + 200
        ),
        weekly: [1000, 1200, 1500, 1800],
        monthly: [2100, 2400, 2700, 3000, 3200, 3500],
        yearly: [25000, 28000, 32000, 35000, 38000],
      },
      token: {
        daily: Array.from(
          { length: 30 },
          (_, i) => Math.floor(Math.random() * 100) + 50
        ),
        weekly: [200, 250, 300, 400],
        monthly: [500, 600, 750, 850, 900, 1000],
        yearly: [6000, 7200, 8500, 9200, 10000],
      },
      failedPayments: {
        daily: Array.from(
          { length: 30 },
          (_, i) => Math.floor(Math.random() * 10) + 1
        ),
        weekly: [15, 12, 8, 10],
        monthly: [6, 4, 7, 9, 5, 8],
        yearly: [80, 65, 45, 55, 40],
      },
      refunds: {
        daily: Array.from(
          { length: 30 },
          (_, i) => Math.floor(Math.random() * 20) + 5
        ),
        weekly: [100, 80, 120, 90],
        monthly: [110, 95, 105, 85, 120, 100],
        yearly: [1200, 1000, 1400, 1100, 1300],
      },
    };

    let categories = [];
    let data = [];
    const period = currentFilter?.filter || "weekly";

    switch (period) {
      case "daily":
        categories = Array.from({ length: 30 }, (_, i) => `Day ${i + 1}`);
        data = baseData[type]?.daily || [];
        break;
      case "weekly":
        categories = ["Week 1", "Week 2", "Week 3", "Week 4"];
        data = baseData[type]?.weekly || [];
        break;
      case "monthly":
        categories = ["Jan", "Feb", "Mar", "Apr", "May", "Jun"];
        data = baseData[type]?.monthly || [];
        break;
      case "yearly":
        categories = ["2020", "2021", "2022", "2023", "2024"];
        data = baseData[type]?.yearly || [];
        break;
      case "custom":
        // For custom date range, generate data based on date difference
        const daysDiff =
          startDate && endDate
            ? Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24))
            : 7;
        categories = Array.from(
          { length: Math.min(daysDiff, 30) },
          (_, i) => `Day ${i + 1}`
        );
        data = Array.from({ length: categories.length }, () =>
          Math.floor(Math.random() * (baseData[type]?.weekly?.[0] || 100))
        );
        break;
      default:
        categories = ["Week 1", "Week 2", "Week 3", "Week 4"];
        data = baseData[type]?.weekly || [];
    }

    return { categories, series: [{ name: type, data }] };
  };

  // Generate Revenue Trend (MRR) data
  const generateMRRData = () => {
    const months = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];
    const mrrData = [
      45000, 48000, 52000, 55000, 58000, 62000, 65000, 68000, 71000, 74000,
      77000, 80000,
    ];

    return {
      categories: months,
      series: [
        {
          name: "Monthly Recurring Revenue (MRR)",
          data: mrrData,
        },
      ],
    };
  };

  // Generate Revenue by Tier data
  const generateRevenueByTierData = () => {
    return {
      labels: ["Basic Tier", "Premium Tier", "Pro Tier"],
      series: [35, 40, 10], // Percentages
    };
  };

  // Generate data based on current filter
  const subscriptionData = generateMockData("subscription", filter);
  const tokenData = generateMockData("token", filter);
  const failedPaymentsData = generateMockData("failedPayments", filter);
  const refundsData = generateMockData("refunds", filter);

  // New chart data
  const mrrTrendData = generateMRRData();
  const revenueByTierData = generateRevenueByTierData();

  return (
    <>
      <section className="position-relative">
        <Container>
          {/* KPIs */}
          <Row>
            <Col lg="12" className="my-2">
              <FeatureCard dashBoardData={dashBoardData?.data} />
            </Col>
          </Row>

        </Container>
      </section>
    </>
  );
};

export default Dashboard;
